import { slugify } from "@growth/schema";
import { Button, Field, Input } from "@growth/ui";
import { Building2, LoaderCircle } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive, useSwitchActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatDate } from "../lib/format";
import { useCreateWorkspace, usePlatformWorkspaces } from "../lib/workspace";

/** Platform admins: every business on Growth Engine, and adding a new one. */
export const PlatformPage = () => {
  const { me } = useActive();
  const switchActive = useSwitchActive();
  const workspaces = usePlatformWorkspaces();
  const memberOf = new Set(me?.workspaces.map((workspace) => workspace.id));

  return (
    <section className="max-w-4xl space-y-8">
      <PageHeader eyebrow="Platform" title="Workspaces">
        One workspace per business. Creating one gives no access by itself: you see a workspace's
        data only if you're a member.
      </PageHeader>

      <CreateWorkspace />

      {workspaces.isError && <ErrorText>{apiErrorMessage(workspaces.error)}</ErrorText>}
      <ul className="divide-y divide-line rounded-xl border border-line bg-white">
        {workspaces.isPending && (
          <li className="px-4 py-6">
            <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
          </li>
        )}
        {workspaces.data?.map((workspace) => (
          <li
            key={workspace.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
          >
            <span>
              <strong className="font-medium">{workspace.name}</strong>{" "}
              <span className="text-muted">
                · {workspace.slug} · {workspace.members} member{workspace.members === 1 ? "" : "s"}{" "}
                · {workspace.brands} brand{workspace.brands === 1 ? "" : "s"} · since{" "}
                {formatDate(workspace.createdAt)}
                {workspace.selfServe ? " · signed up themselves" : ""}
              </span>
            </span>
            {memberOf.has(workspace.id) && (
              <Button variant="secondary" onClick={() => void switchActive(workspace.id, null)}>
                Open
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const CreateWorkspace = () => {
  const create = useCreateWorkspace();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    ownerEmail: "",
    timeZone: "Australia/Melbourne",
    addMe: true,
  });
  const [slugTouched, setSlugTouched] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(form, {
      onSuccess: () => {
        setForm({
          name: "",
          slug: "",
          ownerEmail: "",
          timeZone: "Australia/Melbourne",
          addMe: true,
        });
        setSlugTouched(false);
      },
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <h2 className="flex items-center gap-2 font-serif text-lg text-ink">
        <Building2 className="size-5" />
        New workspace
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Business name">
          {(props) => (
            <Input
              {...props}
              required
              value={form.name}
              onChange={(event) =>
                setForm({
                  ...form,
                  name: event.target.value,
                  slug: slugTouched ? form.slug : slugify(event.target.value),
                })
              }
            />
          )}
        </Field>
        <Field label="Address" hint="Lower case and hyphens">
          {(props) => (
            <Input
              {...props}
              required
              value={form.slug}
              onChange={(event) => {
                setSlugTouched(true);
                setForm({ ...form, slug: event.target.value });
              }}
            />
          )}
        </Field>
        <Field
          label="Owner's email"
          hint="We email them an invite. They become an owner when they sign in with it"
        >
          {(props) => (
            <Input
              {...props}
              type="email"
              required
              value={form.ownerEmail}
              onChange={(event) => setForm({ ...form, ownerEmail: event.target.value })}
            />
          )}
        </Field>
        <Field label="Time zone">
          {(props) => (
            <Input
              {...props}
              required
              value={form.timeZone}
              onChange={(event) => setForm({ ...form, timeZone: event.target.value })}
            />
          )}
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.addMe}
          onChange={(event) => setForm({ ...form, addMe: event.target.checked })}
        />
        Add me as an owner too
      </label>
      {create.isError && <ErrorText>{apiErrorMessage(create.error)}</ErrorText>}
      <Button type="submit" disabled={create.isPending}>
        {create.isPending && <LoaderCircle className="size-4 animate-spin" />}
        Create workspace
      </Button>
    </form>
  );
};
